<template>
    <div class="wizard-step">
        <b-row>
            <b-col cols="12">
                <StepIndicator :current-step="currentStep" :config="config" class="mb-5" @show-terms="$emit('show-terms')" />
                <h3>Sign Your Agreement</h3>
                <p class="mt-4">Review the document below, then sign electronically to complete your enrollment.</p>
            </b-col>
        </b-row>

        <!-- PDF Document Review Section -->
        <b-row class="mt-4">
            <b-col cols="12">
                <b-card class="document-review-card">
                    <h5><i class="fas fa-file-pdf mr-2"></i>Your Agreement Document</h5>
                    <p class="text-muted mb-3">
                        Please scroll through and review the entire agreement before signing.
                        If the document does not display properly, use the "Download &amp; Review" button below to review it separately.
                    </p>
                    <PdfDocumentViewer
                        v-if="previewPdfUrl"
                        :pdf-url="previewPdfUrl"
                        @page-viewed="onPageViewed"
                        @all-pages-viewed="onAllPagesViewed"
                        @audit-event="$emit('audit-event', $event)"
                    />
                    <div v-else class="pdf-loading-placeholder">
                        <i class="fas fa-spinner fa-spin fa-2x"></i>
                        <p class="mt-3 mb-0">Loading your agreement...</p>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <!-- Document review reminder (shown if not all pages viewed) -->
        <b-row v-if="!allPagesViewed" class="mt-4">
            <b-col cols="12">
                <b-alert show variant="info">
                    <i class="fas fa-scroll mr-2"></i>
                    <strong>Please review the document above</strong><br>
                    Scroll through the entire agreement document to enable the signature section.
                </b-alert>
            </b-col>
        </b-row>

        <!-- Signature Capture Section (shown after document review) -->
        <transition name="fade">
            <b-row v-if="allPagesViewed" class="mt-4">
                <b-col cols="12">
                    <b-card class="signature-card">
                        <h5><i class="fas fa-pen-fancy mr-2"></i>Your Signature</h5>
                        <p class="text-muted mt-2 mb-4">
                            Sign your name below using your mouse, touchpad, or finger on mobile devices.
                        </p>
                        <div class="signature-container">
                            <SignatureCanvas
                                ref="signatureCanvas"
                                :width="signatureWidth"
                                :height="200"
                                :allow-typed="true"
                                @signature-changed="onSignatureChanged"
                                @signature-started="onSignatureStarted"
                                @signature-cleared="onSignatureCleared"
                            />
                        </div>
                        <div class="signature-actions mt-3">
                            <b-button
                                variant="outline-primary"
                                size="sm"
                                @click="clearSignature"
                                :disabled="!hasSignature"
                            >
                                <i class="fas fa-eraser mr-1"></i>Clear Signature
                            </b-button>
                        </div>
                    </b-card>
                </b-col>
            </b-row>
        </transition>

        <!-- Validation info box -->
        <b-row class="mt-4" v-if="!isValid">
            <b-col cols="12">
                <b-alert show variant="warning">
                    <i class="fas fa-exclamation-triangle mr-2"></i>
                    <strong>To complete your agreement:</strong>
                    <ul class="mb-0 mt-2">
                        <li v-if="!allPagesViewed">Review the entire agreement document above</li>
                        <li v-if="allPagesViewed && !hasSignature">Provide your signature in the signature box</li>
                    </ul>
                </b-alert>
            </b-col>
        </b-row>

        <!-- Ready to sign confirmation -->
        <b-row v-if="isValid" class="mt-4">
            <b-col cols="12">
                <b-alert show variant="success">
                    <i class="fas fa-check-circle mr-2"></i>
                    <strong>Ready to sign!</strong>
                    Click "Sign {{ config.shortTitle || 'Agreement' }}" below to complete your agreement.
                </b-alert>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import {apiUrl} from '@/constants/config';
import StepIndicator from '@/components/sign-agreement/StepIndicator.vue';
import SignatureCanvas from '@/components/SignatureCanvas.vue';
import PdfDocumentViewer from '@/components/PdfDocumentViewer.vue';

export default {
    name: 'SignDocument',
    components: {
        StepIndicator,
        SignatureCanvas,
        PdfDocumentViewer
    },
    props: {
        plan: {
            type: Object,
            required: true
        },
        formData: {
            type: Object,
            required: true
        },
        currentStep: {
            type: Number,
            required: true
        },
        apiPath: {
            type: String,
            required: true
        },
        consentData: {
            type: Object,
            default: () => ({})
        },
        config: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            hasSignature: false,
            signatureStartTimestamp: null,
            windowWidth: window.innerWidth,
            // PDF viewing state
            viewedPages: new Set(),
            allPagesViewed: false
        };
    },
    computed: {
        isValid() {
            return this.allPagesViewed && this.hasSignature;
        },
        signatureWidth() {
            // Responsive signature pad width
            if (this.windowWidth < 576) {
                return Math.min(this.windowWidth - 80, 400);
            }
            return 500;
        },
        previewPdfUrl() {
            if (!this.apiPath) return null;
            return `${apiUrl}${this.apiPath}/preview-pdf`;
        }
    },
    watch: {
        isValid(newVal) {
            this.$emit('validation-changed', newVal);
        }
    },
    mounted() {
        // Listen for window resize
        window.addEventListener('resize', this.handleResize);

        // Emit step entered event for audit
        this.$emit('audit-event', {
            eventType: 'step_5_entered',
            data: { stepName: 'sign_document' }
        });

        // Record document presented event
        this.$emit('document-presented', {
            timestamp: new Date().toISOString()
        });
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        handleResize() {
            this.windowWidth = window.innerWidth;
        },

        onPageViewed(pageNum) {
            this.viewedPages.add(pageNum);
        },

        onAllPagesViewed(data) {
            this.allPagesViewed = true;
            this.$emit('audit-event', {
                eventType: 'document_fully_reviewed',
                data: { totalPages: data.totalPages }
            });
        },

        onSignatureChanged(hasSignature) {
            this.hasSignature = hasSignature;
        },

        onSignatureStarted() {
            if (!this.signatureStartTimestamp) {
                this.signatureStartTimestamp = new Date().toISOString();
                this.$emit('signature-started', {
                    timestamp: this.signatureStartTimestamp
                });
            }
        },

        onSignatureCleared() {
            this.signatureStartTimestamp = null;
            this.$emit('signature-cleared', {
                timestamp: new Date().toISOString()
            });
        },

        clearSignature() {
            if (this.$refs.signatureCanvas) {
                this.$refs.signatureCanvas.clear();
            }
        },

        getSignatureData() {
            if (!this.$refs.signatureCanvas) {
                return null;
            }
            const signatureData = this.$refs.signatureCanvas.getSignatureData();
            if (signatureData) {
                return {
                    ...signatureData,
                    consentTimestamp: this.consentData.consentTimestamp,
                    signatureStartTimestamp: this.signatureStartTimestamp,
                    signatureCompletedTimestamp: new Date().toISOString()
                };
            }
            return null;
        }
    }
};
</script>

<style lang="scss" scoped>
    .card {
        border: 1px solid #e9ecef;
        box-shadow: 0 2px 4px rgba(0,0,0,0.05);

        h5 {
            color: #464577;
            margin-bottom: 15px;
        }
    }

    .document-review-card {
        border-left: 4px solid #17a2b8;

        h5 {
            color: #464577;
        }
    }

    .pdf-loading-placeholder {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 300px;
        background: #f5f5f5;
        border-radius: 8px;
        color: #666;
    }

    .signature-card {
        border-left: 4px solid #28a745;

        .signature-container {
            display: flex;
            justify-content: center;
        }

        .signature-actions {
            display: flex;
            justify-content: center;
        }
    }

    // Transition
    .fade-enter-active, .fade-leave-active {
        transition: opacity 0.3s ease, transform 0.3s ease;
    }

    .fade-enter, .fade-leave-to {
        opacity: 0;
        transform: translateY(-10px);
    }
</style>
